import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Product } from "./products";

export interface CartItem {
  productId: string;
  slug: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  stockLimit: number;
}

interface CartStore {
  items: CartItem[];
  isDrawerOpen: boolean;
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  itemCount: () => number;
  subtotal: () => number;
}

// Math.min por fora: com estoque zerado (max < min), Math.max(min, ...) por
// fora sempre devolveria min (1) mesmo sem estoque nenhum.
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

export const useCart = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      isDrawerOpen: false,
      addItem: (product, quantity = 1) => {
        if (product.is_custom_quote || product.price == null || product.stock_quantity <= 0) return;
        set((state) => {
          const existing = state.items.find((i) => i.productId === product.id);
          if (existing) {
            const next = clamp(existing.quantity + quantity, 1, product.stock_quantity);
            return {
              items: state.items.map((i) =>
                i.productId === product.id ? { ...i, quantity: next } : i,
              ),
            };
          }
          const q = clamp(quantity, 1, product.stock_quantity);
          return {
            items: [
              ...state.items,
              {
                productId: product.id,
                slug: product.slug,
                name: product.name,
                price: product.price!,
                image: product.images[0] ?? "",
                quantity: q,
                stockLimit: product.stock_quantity,
              },
            ],
          };
        });
      },
      removeItem: (productId) =>
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        })),
      updateQuantity: (productId, quantity) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId ? { ...i, quantity: clamp(quantity, 1, i.stockLimit) } : i,
          ),
        })),
      clearCart: () => set({ items: [] }),
      openDrawer: () => set({ isDrawerOpen: true }),
      closeDrawer: () => set({ isDrawerOpen: false }),
      itemCount: () => get().items.reduce((s, i) => s + i.quantity, 0),
      subtotal: () => get().items.reduce((s, i) => s + i.price * i.quantity, 0),
    }),
    {
      name: "eg-eletrica-cart-v1",
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
      partialize: (state) => ({ items: state.items }),
    },
  ),
);
